import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import { photoSrcSet } from '@/lib/albums'
import { momentPhotoUrl, paragraphs, type MomentSummary, type NowPerson } from '@/lib/moments'
import { cn } from '@/lib/utils'
import { toldWhen } from '@/lib/moments'
import { LiveDot } from '@/components/MomentStyle'

// Which Now moments you've played, kept on this device so a ring turns gray once you've seen all of someone's.
const SEEN_KEY = 'gatheredfrom:now-seen'
const SEEN_KEPT = 400
const listeners = new Set<() => void>()
let seenCache: string[] | null = null
function seenList(): string[] {
  if (seenCache) return seenCache
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as unknown
    seenCache = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : []
  } catch {
    seenCache = []
  }
  return seenCache
}
function markSeen(id: string) {
  const list = seenList()
  if (list.includes(id)) return
  seenCache = [...list, id].slice(-SEEN_KEPT)
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seenCache))
  } catch {
    // Private browsing or full storage: rings simply stay bright.
  }
  listeners.forEach((l) => l())
}
const useSeen = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    seenList,
    () => [] as string[],
  )

// Happening now, across the top of Modern moments: a ring for each person with Now moments from the last few days,
// in the logo's colours until you've seen them all. Tapping one plays their moments full screen, then the next
// person's. Unlike stories, nothing disappears: each is still a moment, on the wall and on its own page.
export function NowRings({ people }: { people: NowPerson[] }) {
  const seen = useSeen()
  const [playing, setPlaying] = useState<number | null>(null)
  if (people.length === 0) return null
  const fresh = (p: NowPerson) => p.moments.some((x) => !seen.includes(x.id))
  return (
    <>
      <ul
        aria-label="Happening now"
        className="-mx-5 flex snap-x [scrollbar-width:none] gap-3.5 overflow-x-auto px-5 pt-1 pb-2 sm:-mx-8 sm:px-8 nav:mx-0 nav:justify-center nav:px-0"
      >
        {people.map((person, i) => (
          <li key={person.author.id} className="snap-start">
            <button
              type="button"
              onClick={() => setPlaying(i)}
              className="group flex w-[4.5rem] flex-col items-center gap-1.5 rounded-xl text-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <span
                className={cn(
                  'grid size-[4.25rem] place-items-center rounded-full p-[3px] transition-transform duration-300 ease-out group-active:scale-95',
                  fresh(person) ? 'now-ring' : 'bg-border',
                )}
              >
                <span className="grid size-full place-items-center rounded-full bg-background p-[2px]">
                  <Avatar person={person.author} className="size-full text-lg shadow-none" />
                </span>
              </span>
              <span className="w-full truncate text-center">
                <span className="sr-only">{fresh(person) ? 'New from ' : 'Seen: '}</span>
                {person.author.name.split(/\s+/)[0]}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {playing !== null &&
        createPortal(<NowPlayer people={people} start={playing} onClose={() => setPlaying(null)} />, document.body)}
    </>
  )
}

const HOLD = { words: 7000, photo: 6000 }

// One person's Now moments full screen, one after another, then the next person's. Tap the right side for the next,
// the left for the one before; hold to pause; swipe down or Escape to close.
function NowPlayer({ people, start, onClose }: { people: NowPerson[]; start: number; onClose: () => void }) {
  const [at, setAt] = useState({ person: start, moment: firstUnseen(people[start]!) })
  const [paused, setPaused] = useState(false)
  const [progress, setProgress] = useState(0)
  const [drag, setDrag] = useState(0)
  const touch = useRef<{ x: number; y: number; t: number } | null>(null)
  const person = people[at.person]!
  const moment = person.moments[at.moment]!
  const photo = moment.photos[0]
  const words = !photo
  const length = words ? HOLD.words : HOLD.photo

  const next = useCallback(() => {
    setProgress(0)
    setAt((a) => {
      const p = people[a.person]!
      if (a.moment + 1 < p.moments.length) return { ...a, moment: a.moment + 1 }
      if (a.person + 1 < people.length) return { person: a.person + 1, moment: firstUnseen(people[a.person + 1]!) }
      onClose()
      return a
    })
  }, [people, onClose])
  const back = useCallback(() => {
    setProgress(0)
    setAt((a) => {
      if (a.moment > 0) return { ...a, moment: a.moment - 1 }
      if (a.person > 0) return { person: a.person - 1, moment: 0 }
      return a
    })
  }, [])

  useEffect(() => markSeen(moment.id), [moment.id])

  // The bar fills while the moment shows, and moves on when it's full. Holding pauses it where it is.
  const shown = useRef(0)
  useEffect(() => {
    shown.current = 0
  }, [moment.id])
  useEffect(() => {
    if (paused) return
    let last = performance.now()
    let frame = requestAnimationFrame(function tick(now) {
      shown.current += now - last
      last = now
      if (shown.current >= length) {
        shown.current = 0
        next()
        return
      }
      setProgress(shown.current / length)
      frame = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frame)
  }, [paused, length, next, moment.id])

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') back()
    }
    window.addEventListener('keydown', key)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', key)
      document.body.style.overflow = overflow
    }
  }, [next, back, onClose])

  const told = toldWhen(moment)
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${person.author.name}, happening now`}
      className="now-player fixed inset-0 z-50 overflow-hidden bg-black text-white select-none"
      style={{
        transform: drag ? `translateY(${drag}px) scale(${1 - drag / 2000})` : undefined,
        borderRadius: drag ? 24 : undefined,
        transition: drag ? 'none' : undefined,
      }}
      onPointerDown={() => setPaused(true)}
      onPointerUp={() => setPaused(false)}
      onPointerCancel={() => setPaused(false)}
      onTouchStart={(e) => (touch.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY, t: Date.now() })}
      onTouchMove={(e) => {
        const start = touch.current
        if (start) setDrag(Math.max(0, e.touches[0]!.clientY - start.y))
      }}
      onTouchEnd={() => {
        if (drag > 120) onClose()
        setDrag(0)
        touch.current = null
      }}
    >
      {photo ? (
        <>
          <img
            key={`bg-${photo.id}`}
            src={momentPhotoUrl(photo.id, 'thumb')}
            alt=""
            aria-hidden
            className="absolute inset-0 size-full scale-110 object-cover opacity-50 blur-2xl"
          />
          <img
            key={photo.id}
            src={momentPhotoUrl(photo.id, 'display')}
            srcSet={photoSrcSet({ ...photo, ref: photo.id }, ['medium', 'display'], momentPhotoUrl)}
            sizes="100vw"
            alt=""
            className="now-frame absolute inset-0 size-full object-contain"
          />
        </>
      ) : (
        <div key={moment.id} className="now-frame absolute inset-0 grid place-items-center bg-viewer px-8">
          <div className="flex max-w-xl flex-col gap-5">
            <span aria-hidden className="bg-logo h-1 w-10 rounded-full" />
            <p className="text-[1.9rem] leading-tight font-medium text-pretty sm:text-4xl">
              {moment.body.trim() || moment.title}
            </p>
          </div>
        </div>
      )}
      <div aria-hidden className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/75 to-transparent" />

      <div className="absolute inset-x-0 top-0 flex flex-col gap-3 px-3 pt-[calc(var(--safe-top,0px)+0.75rem)]">
        <div className="flex gap-1" aria-hidden>
          {person.moments.map((x, i) => (
            <span key={x.id} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/30">
              <span
                className="block h-full rounded-full bg-white"
                style={{ width: `${i < at.moment ? 100 : i === at.moment ? progress * 100 : 0}%` }}
              />
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2.5 px-1">
          <Avatar person={person.author} className="size-9 text-sm shadow-none" />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-semibold">{person.author.name}</p>
            <p className="flex items-center gap-1.5 text-xs text-white/80">
              {told.live && <LiveDot />}
              {told.text}
              {person.moments.length > 1 && ` · ${at.moment + 1} of ${person.moments.length}`}
            </p>
          </div>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onClose}
            aria-label="Close"
            className="grid size-11 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X aria-hidden />
          </button>
        </div>
      </div>

      {/* Tap zones: the left third goes back, the rest goes on. */}
      <button
        type="button"
        aria-label="Previous"
        onClick={back}
        className="absolute top-24 bottom-40 left-0 w-1/3 outline-none"
      >
        <ChevronLeft className="sr-only" />
      </button>
      <button
        type="button"
        aria-label="Next"
        onClick={next}
        className="absolute top-24 right-0 bottom-40 w-2/3 outline-none"
      >
        <ChevronRight className="sr-only" />
      </button>

      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-4 px-5 pb-[calc(var(--safe-bottom,0px)+1.25rem)]">
        {!words && (
          <div className="flex max-w-xl flex-col gap-1.5">
            <p className="text-2xl font-semibold tracking-tight text-balance">{moment.title}</p>
            {moment.body && <p className="line-clamp-3 text-[0.95rem] text-white/90">{paragraphs(moment.body)[0]}</p>}
          </div>
        )}
        <Link
          to={`/moment/${moment.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="grid h-13 min-h-12 place-items-center rounded-full bg-white px-6 text-base font-semibold text-viewer outline-none focus-visible:ring-4 focus-visible:ring-white/50 sm:self-start"
        >
          Open the moment
        </Link>
      </div>
    </div>
  )
}

// Where to start in someone's moments: the first you haven't seen, or the first of all.
function firstUnseen(person: NowPerson) {
  const seen = seenList()
  const i = person.moments.findIndex((x: MomentSummary) => !seen.includes(x.id))
  return i < 0 ? 0 : i
}
